import React from "react";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaModalIconWrapper from "../../ui-kit/kuika-modal-icon-wrapper";
import KuikaH5 from "../../ui-kit/typography/H5/H5";
import KuikaText from "../../ui-kit/typography/Text/Text";
import { BRANCH_NAME_MAX_LENGTH, IBranchNameValidationResult, validateBranchName } from "./branch-name-validation";
import "./branching-modal.scss";

export interface IRenameBranchModalProps {
  showModal: boolean;
  branchName: string;
  currentBranchName: string;
  existingBranchNames: readonly string[];
  /**
   * Where the rename stands (`KuikaButton` `status`): the busy word, then its ✓ or "Try again".
   */
  status?: KuikaButtonStatus;
  onClose: () => void;
  onBranchNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRenameBranch: () => void;
}

const RENAME_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Renaming", success: "Renamed" };

const RenameBranchModal: React.FC<IRenameBranchModalProps> = ({
  showModal,
  branchName,
  currentBranchName,
  existingBranchNames,
  status,
  onClose,
  onBranchNameChange,
  onRenameBranch
}) => {
  const validation: IBranchNameValidationResult = validateBranchName(branchName, {
    existingNames: existingBranchNames,
    currentName: currentBranchName
  });
  const unchanged: boolean = branchName.trim() === currentBranchName.trim();
  // While the ✓ is up the branch already carries the new name, so the field reads as unchanged
  // and taken — the check is the answer then, not the rule.
  const isConfirming: boolean = status === "success";
  const showError: boolean = !validation.valid && branchName.length > 0 && !isConfirming;

  const footer = (
    <KModalFooter
      closesOnSuccess
      onCancel={onClose}
      submitLabel="Save"
      onSubmit={onRenameBranch}
      status={status}
      statusLabels={RENAME_STATUS_LABELS}
      disabled={(!validation.valid || unchanged) && !isConfirming && status !== "loading"}
    />
  );

  return (
    <KModal showModal={showModal} closeModal={onClose} header="Rename Branch" footer={footer} width={360} centered>
      <div className="kuika-branching-modal">
        <div className="kuika-branching-modal__header">
          <KuikaModalIconWrapper iconName="edit" intent="edit" />
          <div className="kuika-branching-modal__title">
            <KuikaH5>Rename Branch</KuikaH5>
          </div>
        </div>
        <div className="kuika-branching-modal__content">
          <div className="kuika-branching-modal__description">
            <KuikaText>Type down the new branch name down below</KuikaText>
          </div>
          <div className="kuika-branching-modal__form">
            <div className="kuika-branching-modal__form-item">
              <KuikaInput
                placeholder="Branch Name"
                value={branchName}
                onChange={onBranchNameChange}
                size="lg"
                autoFocus
                maxLength={BRANCH_NAME_MAX_LENGTH}
                error={showError}
              />
              {showError && (
                <div className="kuika-branching-modal__field-error">
                  <KuikaText>{validation.error}</KuikaText>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </KModal>
  );
};

export default RenameBranchModal;
